import { Theme } from '@mui/system';
import { z } from 'zod';
import { ICanonicalSchemaDefinitionField } from '../canonicalSchemas/canonicalSchemaUtils';
import { DeepReadonly, DeepReadonlyObject } from '../utils/utilTypes';
import { GQCanonicalDatasetKind, Maybe } from '../generated/graphql';
import { DateTime } from 'luxon';
import { DuckDiffOperation } from '../canonicalSchemas/DuckDiffOutputSchema';

// ValueMappingConfig types

export type ValueMappingColumn =
  | ValueMappingKeyColumn
  | ValueMappingOutputColumn
  | ValueMappingContextColumn;

export type ValueMappingColumnBase = ICanonicalSchemaDefinitionField & {
  zod?: z.ZodType; // Used for JSON fields stored as strings
  isCurrency?: boolean;
  isHiddenByDefault?: boolean; // Used to determine columns shown by default in frontend mapping table
};

// The `isRequired` and `isFallback` properties are mutually exclusive: a
// required column cannot be a fallback, and a fallback column cannot be
// required.
export type ValueMappingKeyColumn =
  | (ValueMappingColumnBase & {
      isRequired: true;
      // isFallback=true means the keyColumn will be excluded from group id
      isFallback?: false;
    })
  | (ValueMappingColumnBase & {
      isRequired?: false;
      isFallback: true;
    })
  | (ValueMappingColumnBase & {
      isRequired?: false;
      isFallback?: false;
    });

export type ValueMappingOutputColumn = ValueMappingColumnBase & {
  isRequiredForPublish?: boolean;
};

/**
 * Typeguard to narrow a ValueMappingColumn to an output column type
 */
export function isValueMappingOutputColumn(
  column: ValueMappingColumn | DeepReadonlyObject<ValueMappingColumn>
): column is ValueMappingOutputColumn {
  return (
    (column as ValueMappingOutputColumn).isRequiredForPublish !== undefined
  );
}

/**
 * Helpful, unchanging user data that informs a human’s mapping decision,
 * typically populated by user data. This is metadata and stored separately from
 * the parquet artifact.
 */
export type ValueMappingContextColumn = ValueMappingColumnBase & {
  /**
   * Whether the value is a cumulative sum (e.g. the cumulative spend for a
   * financial mapping)
   */
  isCumulative?: boolean;
  /**
   * Whether the value is a minimum of existing and incoming input rows (e.g. earliest start date
   * for methodology customization activity rows)
   */
  isMinimum?: boolean;

  /**
   * Whether the value is a maximum of existing and incoming input rows (e.g. latest end date
   * for methodology customization activity rows)
   */
  isMaximum?: boolean;

  /** Whether the value is a JSON Array/Set  */
  isSet?: boolean;
};

export type ValueMappingConfig =
  | SimpleValueMappingConfig
  | MultiOutputValueMappingConfig;

type ValueMappingConfigBase = {
  name: string;

  // Version
  // e.g. '2023-01-01.1', like for BATs and Emissions models. The leading decimal dictates order,
  // (e.g. 2022-04-01.3 is the third version released on that date).
  // If null, the version is not live and available for use.
  // In this case generation SQL or migration SQL may not exist yet.
  // This is useful for communicating to engineers that a new version is WIP
  liveAsOf: string | null;
  // e.g. papaya_column, used to identify the version when writing generation sql and saving reference data.
  versionId: string;
  // e.g. "populating user_upload_fmds with fmd_ids instead of usrupld_ids"
  versionDescription: string;

  // Columns
  keyColumns: Record<string, ValueMappingKeyColumn>;
  contextColumns: Record<string, ValueMappingContextColumn>;
};

export type SimpleValueMappingConfig = DeepReadonly<
  ValueMappingConfigBase & {
    _type: 'simple';
    outputColumns: Record<string, ValueMappingOutputColumn>;
  }
>;

// Not used anywhere yet, but this is an example of how we could support more
// complex mapping configs.
type MultiOutputValueMappingConfig = DeepReadonly<
  ValueMappingConfigBase & {
    _type: 'multi_output';
    mappingOutputs: Array<{
      name: string;
      columns: Record<string, ValueMappingOutputColumn>;
    }>;
  }
>;

export type ValueMappingRecordType = string | boolean | number | null | Date;
export type ValueMappingRecord = Record<string, ValueMappingRecordType>;
export type ValueMappingRecords = Array<ValueMappingRecord>;

// Extra row appended from response when querying via new DuckDB-ified loading path
export const NON_FALLBACK_ROW_COUNT_COL =
  'value_mapping_total_non_fallback_rows';

// TODO: maybe dedupe a bit with above if possible?
export type InputValueMappingRecord = Record<
  string,
  // NOTE: object is for suggestions only, which is a bit wonky type-wise
  string | boolean | number | object | null | Array<string>
>;

export type InputValueMappingRecords = Array<InputValueMappingRecord>;

// TODO (DI-4503): use this type in loadRecords, add in start_date, end_date, vendor_override
export const ValueMappingRecordsZod = z.array(
  z.record(
    z.string(),
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.record(z.any()),
      z.array(z.any()),
    ])
  )
);

const valueMappingAutomapperSuggestionSchema = z.object({
  explanation_code: z.string(),
  explanation_label: z.string(),
  probability: z.number(),
  value: z.string(),
  version: z.string(),
});

export const ValueMappingAutomapperSuggestionZod = z.array(
  valueMappingAutomapperSuggestionSchema
);

/**
 * A single suggestion
 */
export type ValueMappingAutomapperSuggestion = z.infer<
  typeof valueMappingAutomapperSuggestionSchema
>;

/**
 * Suggestions for a cell (column x row), assumed to be sorted by probability
 */
export type ValueMappingAutomapperSuggestions =
  Array<ValueMappingAutomapperSuggestion>;

/**
 * Key is column name, value is array of suggestions
 */
export type ValueMappingRowSuggestions = {
  [key: string]: ValueMappingAutomapperSuggestions;
};

/**
 * Key is groupId/rowId, value is all the suggestions for the row
 */
export type ValueMappingSuggestionsById = {
  [key: string]: ValueMappingRowSuggestions;
};

export type ValueMappingTableColumn = {
  name: string;
  canonicalSchemaColumn: DeepReadonlyObject<ValueMappingColumn>;
};

// These are special FE-only columns, named purposefully elaborately to avoid col clashing
// with a real mapping column name that might be used.
export const MAPPING_NESTED_TREE_PATH_COL = 'value_mapping_nested_path_col';
export const VALUE_MAPPING_VENDOR_COUNT_COL = 'value_mapping_vendor_count_col';
export const VALUE_MAPPING_VENDOR_SAMPLE_COL =
  'value_mapping_vendor_sample_col';

export type ValueMappingRecordWithId = ValueMappingRecord & {
  id: string;
};

export type ValueMappingRow = ValueMappingRecordWithId & {
  [MAPPING_NESTED_TREE_PATH_COL]?: Array<string>;
  [VALUE_MAPPING_VENDOR_SAMPLE_COL]?: Array<string>;
  [VALUE_MAPPING_VENDOR_COUNT_COL]?: number;
  // suggestions etc.
};

export type ValueMappingRowUpdates = {
  // key'd by the id of the rowId that has an update
  [key: string]: {
    // key'd column field names, ex. 'bea' to mapped value, ex. 'BEA 123'
    // or boolean for "should_exclude"
    [key: string]: string | boolean | null;
  };
};

export type ValueMappingOverrideUpdates = {
  // key'd by the id of the rowId that has an update
  [key: string]: {
    // key'd column field names, ex. 'bea' to mapped value, ex. 'BEA 123'
    // or boolean for "should_exclude"
    [key: string]: boolean;
  };
};

/**
 * Key is the groupID (which should match rowNode.groupingKey)
 */
export type ValueMappingExpandedRows = { [key: string]: boolean };

export type ValueMappingRowState = {
  previousSaveColumn: string | null;
  previousSaveExcluded: boolean | null;
  previousUsername: string | null;
  currentStateColumn: string | null;
  currentStateExcluded: boolean | null;
  renderedAsOptionKey: string | null;
  suggestions: ValueMappingRowSuggestions | null;
};

export type ValueMappingRowStatusData = {
  color: (theme: Theme) => string;
  label: string;
  tooltipDescription: string;
  type: ValueMappingStatusType;
};

export const VALUE_MAPPING_OUTPUT_OPTION_KEY_EXCLUDE = 'SPECIAL_EXCLUDE';
export const VALUE_MAPPING_OUTPUT_OPTION_KEY_SUPPLIER_COMPANY_REQUEST =
  'SPECIAL_COMPANY_CHANGE_REQUEST';

export type ValueMappingTableSourceData = {
  id: string;
  outputOptionPickers: Array<ValueMappingPickerData>;
};

export type ValueMappingPickerData = {
  id: string; // ID is necessary for graphQL arrays or "selection sets"
  columnName: string;
  placeholder: Maybe<string>;
  options: Array<ValueMappingPickerOption>;
};

export type ValueMappingPickerOption = {
  id: string;
  title: string;
  subtitle: string | null;
  descriptionLong: string | null;
  isSuggested: boolean;
  category: string | null;
  extraData: {
    [key: string]: string | boolean | number | DateTime | null;
  };
};

export type ValueMappingBEACodeData = {
  code: string;
  name: string;
  description_short: string | null;
  description_long: string | null;
  category: string | null;
  kgco2e: number | null;
};

export const MAPPINGS_AUTOSAVING_PROGRESS_LABEL = 'Autosaving';
export const MAPPINGS_SAVED_PROGRESS_LABEL = 'Saved';
export const MAPPINGS_APPROVE_BUTTON_LABEL = 'Approve';
export const MAPPINGS_RESET_ALL_BUTTON_LABEL = 'Reset all';
export const MAPPINGS_RESET_ALL_DIALOG_TITLE = 'Reset all changes?';

export const MAPPINGS_SAVE_PROGRESS_TOOLTIP_DISABLED =
  'There are no changes to save.';
export const MAPPINGS_SAVE_PROGRESS_TOOLTIP_DISABLED_FOR_APPROVAL = `Your changes are ready for approval! Click ${MAPPINGS_APPROVE_BUTTON_LABEL} to continue.`;
export const MAPPINGS_SAVE_PROGRESS_TOOLTIP_ENABLED =
  'This will save your progress, but these changes will not affect your footprint until you finish and click approve.';

export const MAPPINGS_APPROVE_TOOLTIP_DISABLED_NO_ROWS =
  'There are no saved changes to approve.';
export const MAPPINGS_APPROVE_TOOLTIP_PUBLISHED =
  'All completed mappings have been saved and approved.';
export const MAPPINGS_APPROVE_TOOLTIP_DISABLED =
  'You can’t approve until all required inputs are completed.';
export const MAPPINGS_APPROVE_TOOLTIP_ENABLED =
  'By approving, your saved mappings will be applied to future footprints using this dataset.';

export const MAPPING_RESET_ALL_TOOLTIP_DISABLED =
  'There are no changes to reset.';
export const MAPPING_RESET_ALL_TOOLTIP_ENABLED =
  'This will reset all your mappings to the last approved version. This cannot be undone.';
export const MAPPING_RESET_ALL_TOOLTIP_WITH_AUTOSAVE_ENABLED =
  'This will reset all your mappings to the version you started with this session. This cannot be undone.';

export const ValueMappingStatusType = {
  EditedUser: 'EditedUser',
  EditedSuggestion: 'EditedSuggestion',
  EmptyOptional: 'EmptyOptional',
  EmptyRequired: 'EmptyRequired',
  EmptyVendorOverride: 'EmptyVendorOverride',
  Saved: 'Saved',
};
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentionally naming the variable the same as the type to mirror enum behavior
export type ValueMappingStatusType =
  (typeof ValueMappingStatusType)[keyof typeof ValueMappingStatusType];

// Right now there's only one mode (flagged or null), but we may in the future
// want to support other types, like "resolved" or "flagged as urgent" vs. "flagged as info"
export const ValueMappingFlagType = {
  Flagged: 'Flagged',
};
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentionally naming the variable the same as the type to mirror enum behavior
export type ValueMappingFlagType =
  (typeof ValueMappingFlagType)[keyof typeof ValueMappingFlagType];

export type CalloutInfoVisibilityState = {
  title: string;
  description: string | null;
};

export enum ChangelogEntryType {
  Comment = 'Comment',
  Update = 'Update',
}

export interface ChangelogCommentEntry {
  type: ChangelogEntryType.Comment;
  value: {
    id: string;
    message: string;
    creator: {
      id: string;
      isWatershedEmployee: boolean;
      isWatershedContractor: boolean;
      name: string;
    };
    lastEditor: {
      id: string;
      isWatershedEmployee: boolean;
      isWatershedContractor: boolean;
      name: string;
    } | null;
    createdAt: Date;
    lastEditedAt: Date | null;
    displayUser: {
      id: string;
      isWatershedEmployee: boolean;
      isWatershedContractor: boolean;
      name: string;
    };
  };
}

export interface ChangelogUpdateEntryValue {
  id: string;
  rowId: string;
  revision: {
    id: string;
    isValueMappingLegacyMigration: null | boolean;
  };
  columnName: string;
  operation: DuckDiffOperation;
  oldValue: null | { type: string; value: unknown };
  newValue: null | { type: string; value: unknown };
  changedAt: Date;
  changedBy: {
    id: string;
    name: string;
    isWatershedEmployee: boolean;
    isWatershedContractor: boolean;
  };
}

export interface ChangelogUpdateEntry {
  type: ChangelogEntryType.Update;
  isForSupplierOverrideRow: boolean;
  value: ChangelogUpdateEntryValue;
}

export type ChangelogType = ChangelogCommentEntry | ChangelogUpdateEntry;

export const historyEntryValueSchema = z
  .object({ type: z.string(), value: z.unknown() })
  .nullable();
type HistoryEntryValue = z.infer<typeof historyEntryValueSchema>;

export function parseHistoryEntryValue(v: null | string): HistoryEntryValue {
  return v === null ? null : historyEntryValueSchema.parse(JSON.parse(v));
}

export interface ValueMappingHistoryEntry {
  id: string;
  rowId: string;
  revisionId: string;
  changedAt: Date;
  changedBy: string;
  columnName: string;
  operation: DuckDiffOperation;
  oldValue: HistoryEntryValue;
  newValue: HistoryEntryValue;
}

export type OneSchemaOutputUutPack = {
  userUploadTaskId: string;
  datasourceId: string;
  datasetId: string;
  uploaderUserId: string;
  canonicalDatasetKind: GQCanonicalDatasetKind;
  userUploadedTableId: string;
  fileMetadataId: string;
  userUploadId: string;
  measurementProjectId: string;
  fileName: string;
};

// Health check
export type ValueMappingOrgHealthCheckResult = {
  uutResults: Array<ValueMappingUutHealthCheckResult>;
  success: boolean;
  orgId: string;
  orgName: string;
};
export type ValueMappingHealthCheckMessageLevel =
  // There is a problem currently in prod risking correctness incident:
  | 'error'

  // No action needed:
  | 'info';
export type ValueMappingHealthCheckMessage = {
  level: ValueMappingHealthCheckMessageLevel;
  message: string;
  data?: any;
};
export type ValueMappingUutHealthCheckResult = {
  uutPack: OneSchemaOutputUutPack;
  messages: Array<ValueMappingHealthCheckMessage>;
  data?: any;
};

export const EXPENDITURE_UNMAPPED_ACTIVITY_TYPE = 'expenditure_unmapped';
export const EXPENDITURE_MAPPED_ACTIVITY_TYPE = 'expenditure_mapped';
export const EXPENDITURE_ACTIVITY_TYPE = 'expenditure';
export type ExpenditureActivityTypes =
  | typeof EXPENDITURE_UNMAPPED_ACTIVITY_TYPE
  | typeof EXPENDITURE_MAPPED_ACTIVITY_TYPE
  | typeof EXPENDITURE_ACTIVITY_TYPE;
